/**
 * EGVP Posteingang - Elektronisches Gerichts- und Verwaltungspostfach
 * Zeigt eingegangene Nachrichten von Gerichten und Behörden
 */

import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Building2, RefreshCw, Mail, MailOpen, FileText, Calendar,
  AlertCircle, CheckCircle2, Clock, Send, ChevronRight,
  Filter, Search, Eye, Download, ArrowLeft, Loader2, Info
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';

const API_URL = process.env.NODE_ENV === 'production' ? '' : (process.env.REACT_APP_BACKEND_URL || '');

export default function EGVPPosteingangPage() {
  const navigate = useNavigate();
  const [nachrichten, setNachrichten] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [egvpStatus, setEgvpStatus] = useState(null);
  const [selectedNachricht, setSelectedNachricht] = useState(null);
  const [filter, setFilter] = useState('alle');
  const [searchQuery, setSearchQuery] = useState('');

  // EGVP Status laden
  const loadStatus = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/kommunikation/egvp/status`);
      if (res.ok) {
        const data = await res.json();
        setEgvpStatus(data);
      }
    } catch (error) {
      console.error('EGVP Status Fehler:', error);
    }
  }, []);

  // Posteingang laden
  const loadNachrichten = useCallback(async (showToast = false) => {
    setRefreshing(true);
    try {
      const res = await fetch(`${API_URL}/api/kommunikation/egvp/posteingang?max_messages=50`);
      if (res.ok) {
        const data = await res.json();
        setNachrichten(data.nachrichten || []);
        if (showToast && data.is_mock) {
          toast.info('EGVP läuft im Test-Modus (Mock-Daten)');
        } else if (showToast) {
          toast.success(`${data.nachrichten?.length || 0} Nachrichten geladen`);
        }
      }
    } catch (error) {
      console.error('Posteingang laden Fehler:', error);
      toast.error('Fehler beim Laden des Posteingangs');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    loadStatus();
    loadNachrichten();
  }, [loadStatus, loadNachrichten]);

  // Nachricht als gelesen markieren
  const markAsRead = async (nachrichtId) => {
    try {
      const res = await fetch(`${API_URL}/api/kommunikation/egvp/nachricht/${nachrichtId}/gelesen`, {
        method: 'POST'
      });
      if (res.ok) {
        setNachrichten(prev => prev.map(n => 
          n.id === nachrichtId ? { ...n, gelesen: true } : n
        ));
      }
    } catch (error) {
      console.error('Als gelesen markieren Fehler:', error);
    }
  };

  // Nachricht anzeigen
  const viewNachricht = async (nachricht) => {
    setSelectedNachricht(nachricht);
    if (!nachricht.gelesen) {
      markAsRead(nachricht.id);
    }
  };

  // Gefilterte Nachrichten
  const filteredNachrichten = nachrichten.filter(n => {
    // Filter anwenden
    if (filter === 'ungelesen' && n.gelesen) return false;
    if (filter === 'mit_anhang' && !n.hat_anhang) return false;
    if (filter === 'gericht' && !n.absender_safe_id?.includes('justiz')) return false;
    if (filter === 'behoerde' && n.absender_safe_id?.includes('justiz')) return false;
    
    // Suche
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      return (
        n.betreff?.toLowerCase().includes(q) ||
        n.absender_name?.toLowerCase().includes(q) ||
        n.aktenzeichen?.toLowerCase().includes(q)
      );
    }
    return true;
  });

  const ungeleseneAnzahl = nachrichten.filter(n => !n.gelesen).length;

  // Kategorie-Badge
  const KategorieBadge = ({ kategorie }) => {
    const configs = {
      beschluss: { color: 'bg-blue-100 text-blue-700', label: 'Beschluss' },
      bescheid: { color: 'bg-green-100 text-green-700', label: 'Bescheid' },
      frist: { color: 'bg-red-100 text-red-700', label: 'Frist' },
      anfrage: { color: 'bg-amber-100 text-amber-700', label: 'Anfrage' },
      default: { color: 'bg-slate-100 text-slate-700', label: kategorie || 'Sonstig' }
    };
    const cfg = configs[kategorie] || configs.default;
    return <Badge className={`${cfg.color} text-xs`}>{cfg.label}</Badge>;
  };

  return (
    <div className="max-w-6xl mx-auto space-y-6" data-testid="egvp-posteingang-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center">
            <Building2 className="w-6 h-6 text-blue-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-800">EGVP Posteingang</h1>
            <p className="text-sm text-slate-500">
              Elektronisches Gerichts- und Verwaltungspostfach
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {egvpStatus?.mock_mode && (
            <Badge variant="outline" className="text-amber-600 border-amber-300">Test-Modus</Badge>
          )}
          <Button variant="outline" onClick={() => navigate('/kommunikation')}>
            <Send className="w-4 h-4 mr-2" />
            Nachricht senden
          </Button>
          <Button onClick={() => loadNachrichten(true)} disabled={refreshing}>
            <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            Abrufen
          </Button>
        </div>
      </div>

      {/* Status Alert */}
      {!egvpStatus?.aktiv && (
        <Alert className="bg-amber-50 border-amber-200">
          <AlertCircle className="w-4 h-4 text-amber-600" />
          <AlertDescription className="text-amber-800">
            EGVP ist nicht konfiguriert. Bitte in{' '}
            <Button variant="link" className="px-0 text-amber-800 underline" onClick={() => navigate('/admin?tab=kommunikation')}>
              Administration → Kommunikation
            </Button>{' '}
            einrichten.
          </AlertDescription>
        </Alert>
      )}

      {/* Info Mock-Modus */}
      {egvpStatus?.mock_mode && egvpStatus?.aktiv && (
        <Alert className="bg-blue-50 border-blue-200">
          <Info className="w-4 h-4 text-blue-600" />
          <AlertDescription className="text-blue-800">
            EGVP läuft im <strong>Test-Modus</strong>. Die angezeigten Nachrichten sind Beispieldaten. 
            Für den Produktivbetrieb deaktivieren Sie den Test-Modus in den EGVP-Einstellungen.
          </AlertDescription>
        </Alert>
      )}

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Nachrichtenliste */}
        <div className="lg:col-span-2 space-y-4">
          {/* Filter & Suche */}
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Suche nach Betreff, Absender, Aktenzeichen..."
                className="pl-10"
              />
            </div>
            <Select value={filter} onValueChange={setFilter}>
              <SelectTrigger className="w-full sm:w-48">
                <Filter className="w-4 h-4 mr-2" />
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="alle">Alle Nachrichten</SelectItem>
                <SelectItem value="ungelesen">Nur ungelesene ({ungeleseneAnzahl})</SelectItem>
                <SelectItem value="mit_anhang">Mit Anhang</SelectItem>
                <SelectItem value="gericht">Von Gerichten</SelectItem>
                <SelectItem value="behoerde">Von Behörden</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Nachrichten */}
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
            </div>
          ) : filteredNachrichten.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <Mail className="w-12 h-12 mx-auto text-slate-300 mb-4" />
                <p className="text-slate-500">
                  {searchQuery || filter !== 'alle' 
                    ? 'Keine Nachrichten gefunden' 
                    : 'Keine Nachrichten im Posteingang'}
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-2">
              {filteredNachrichten.map((nachricht) => (
                <div
                  key={nachricht.id}
                  onClick={() => viewNachricht(nachricht)}
                  className={`p-4 rounded-xl border cursor-pointer transition-all hover:shadow-md ${
                    nachricht.gelesen 
                      ? 'bg-white border-slate-200' 
                      : 'bg-blue-50 border-blue-200 hover:bg-blue-100'
                  } ${selectedNachricht?.id === nachricht.id ? 'ring-2 ring-blue-500' : ''}`}
                  data-testid={`egvp-nachricht-${nachricht.id}`}
                >
                  <div className="flex items-start gap-4">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                      nachricht.gelesen ? 'bg-slate-100' : 'bg-blue-100'
                    }`}>
                      {nachricht.gelesen 
                        ? <MailOpen className="w-5 h-5 text-slate-500" />
                        : <Mail className="w-5 h-5 text-blue-600" />
                      }
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <div>
                          <p className={`font-medium truncate ${!nachricht.gelesen ? 'text-blue-900' : 'text-slate-800'}`}>
                            {nachricht.betreff}
                          </p>
                          <p className="text-sm text-slate-500 truncate">
                            {nachricht.absender_name}
                          </p>
                        </div>
                        <div className="flex flex-col items-end gap-1">
                          <span className="text-xs text-slate-400 whitespace-nowrap">
                            {new Date(nachricht.eingangsdatum).toLocaleDateString('de-DE', {
                              day: '2-digit',
                              month: '2-digit',
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </span>
                          {nachricht.prioritaet === 'hoch' && (
                            <AlertCircle className="w-4 h-4 text-red-500" />
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2 mt-2">
                        {nachricht.kategorie && <KategorieBadge kategorie={nachricht.kategorie} />}
                        {nachricht.aktenzeichen && (
                          <Badge variant="outline" className="text-xs">
                            Az: {nachricht.aktenzeichen}
                          </Badge>
                        )}
                        {nachricht.hat_anhang && (
                          <Badge variant="outline" className="text-xs text-violet-600 border-violet-300">
                            <FileText className="w-3 h-3 mr-1" />
                            Anhang
                          </Badge>
                        )}
                      </div>
                    </div>
                    <ChevronRight className="w-5 h-5 text-slate-300 flex-shrink-0" />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Detail-Ansicht */}
        <div className="space-y-4">
          {selectedNachricht ? (
            <Card>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg">Nachrichtendetails</CardTitle>
                  <Button variant="ghost" size="sm" onClick={() => setSelectedNachricht(null)}>
                    <ArrowLeft className="w-4 h-4" />
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <p className="text-xs text-slate-500">Betreff</p>
                  <p className="font-medium text-slate-800">{selectedNachricht.betreff}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500">Absender</p>
                  <p className="text-sm text-slate-700">{selectedNachricht.absender_name}</p>
                  <p className="text-xs text-slate-400 font-mono">{selectedNachricht.absender_safe_id}</p>
                </div>
                {selectedNachricht.aktenzeichen && (
                  <div>
                    <p className="text-xs text-slate-500">Aktenzeichen</p>
                    <p className="font-medium text-slate-800">{selectedNachricht.aktenzeichen}</p>
                  </div>
                )}
                <div>
                  <p className="text-xs text-slate-500">Eingangsdatum</p>
                  <p className="text-sm text-slate-700">
                    {new Date(selectedNachricht.eingangsdatum).toLocaleString('de-DE')}
                  </p>
                </div>
                {selectedNachricht.hat_anhang && (
                  <div>
                    <p className="text-xs text-slate-500 mb-2">Anhänge</p>
                    <div className="bg-slate-50 rounded-lg p-3 flex items-center gap-3">
                      <FileText className="w-8 h-8 text-violet-500" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">
                          {selectedNachricht.anhang_name || 'Dokument.pdf'}
                        </p>
                        <p className="text-xs text-slate-400">PDF-Dokument</p>
                      </div>
                      <Button variant="ghost" size="sm">
                        <Download className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                )}
                <div className="pt-4 border-t space-y-2">
                  <Button className="w-full" onClick={() => navigate(`/akten?az=${selectedNachricht.aktenzeichen}`)}>
                    <Eye className="w-4 h-4 mr-2" />
                    Zur Akte
                  </Button>
                  <Button variant="outline" className="w-full">
                    <Send className="w-4 h-4 mr-2" />
                    Antworten
                  </Button>
                </div>
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardContent className="py-12 text-center">
                <Mail className="w-12 h-12 mx-auto text-slate-200 mb-4" />
                <p className="text-slate-400">Nachricht auswählen</p>
              </CardContent>
            </Card>
          )}

          {/* Statistik */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm text-slate-600">Statistik</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-4 text-center">
                <div>
                  <p className="text-2xl font-bold text-blue-600">{nachrichten.length}</p>
                  <p className="text-xs text-slate-500">Gesamt</p>
                </div>
                <div>
                  <p className="text-2xl font-bold text-amber-600">{ungeleseneAnzahl}</p>
                  <p className="text-xs text-slate-500">Ungelesen</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* EGVP Info */}
          {egvpStatus && (
            <Card className="bg-slate-50">
              <CardContent className="py-4">
                <div className="flex items-center gap-2 mb-2">
                  <div className={`w-2 h-2 rounded-full ${egvpStatus.aktiv ? 'bg-green-500' : 'bg-slate-300'}`} />
                  <span className="text-sm font-medium text-slate-700">
                    {egvpStatus.aktiv ? 'EGVP verbunden' : 'EGVP nicht aktiv'}
                  </span>
                </div>
                {egvpStatus.postfach_name && (
                  <p className="text-xs text-slate-500">{egvpStatus.postfach_name}</p>
                )}
                {egvpStatus.safe_id && (
                  <p className="text-xs text-slate-400 font-mono truncate">{egvpStatus.safe_id}</p>
                )}
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}
